import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { CheckCircle2, Circle, Clock, Tag, AlertTriangle, ClipboardCheck, LogIn, LogOut, Eye, EyeOff } from 'lucide-react';

import Sidebar from '../layouts/Sidebar';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import CheckboxGroup from '../components/ui/CheckboxGroup';
import { PageLoader } from '../components/ui/Spinner';
import IncidentMap from '../components/Incident/IncidentMap';
import CommandCenterService from '../services/CommandCenterService';
import AnnouncementService from '../services/AnnouncementService';
import AnnouncementMemberService from '../services/AnnouncementMemberService';
import ZoneService from '../services/ZoneService';
import { buildRoster, createSerialQueue } from '../utils/utils';
import {
  PPE_WARNING_MINUTES, DONNING_CHECKLIST_ITEMS, ppeRemainingMinutes, isChecklistComplete, checklistDoneCount,
  hasPendingDoffing, zoneEntryBlocker, DonningModal, DoffingModal,
} from '../components/Incident/PpeScreening';

// On-scene role a team leader can assign to an arrived responder for this incident —
// distinct from their fixed professional role (doctor/nurse/RSO/admin, shown separately).
// Mirrors the fixed list on the server (src/constants/incidentRoles.js).
const INCIDENT_ROLES = [
  'Incident Commander',
  'Triage Officer',
  'Treatment Lead',
  'Decon Officer',
  'Transport Coordinator',
  'Safety Officer',
  'Entry Control Officer',
  'Documentation/Scribe',
  'Liaison',
].map((role) => ({ value: role, label: role }));

// Fallback when an incident hasn't set its own limit yet — mirrors PPE_DURATION_MINUTES
// in constants/ppe.js on the server. The real, per-incident value is
// incident.ppeSafeLimitMinutes, set by the team leader via the Operating Time Control
// below (depends on PPE level and conditions — heat, winter, etc.). Only the warning
// threshold stays fixed.
// Matches the operating conditions a team leader actually has to choose between —
// mirrors constants/ppe.js's documented presets on the server.
const SAFE_LIMIT_OPTIONS = [
  { minutes: 15, label: 'Level C / Standard DECON — 15 min' },
  { minutes: 10, label: 'Level C / Summer or heat risk — 10 min' },
  { minutes: 40, label: 'Level C / Winter condition — 40 min' },
  { minutes: 30, label: 'Level D / Field support — 30 min' },
  { minutes: 50, label: 'Level D / Winter support — 50 min' },
];

const STATUS_CHOICES = [
  { key: 'green', label: 'All Clear / Safe', cls: 'border-green-500 bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400' },
  { key: 'amber', label: 'Engaged / Busy', cls: 'border-orange-400 bg-orange-50 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400' },
  { key: 'red', label: 'Critical / Help Needed', cls: 'border-red-500 bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400' },
];

const STATUS_DOT_CLS = { Accepted: 'bg-green-500', Rejected: 'bg-red-500' };
// Left-border accent for the larger roster cards — same status keys as STATUS_DOT_CLS,
// styled after the color-coded left border on the Patient Board's big-display cards.
const STATUS_BORDER_CLS = { Accepted: 'border-green-500', Rejected: 'border-red-500' };

// The command-center payload carries the hazard types as an array (`types`) — there's
// no single `type`, which is what used to print "… - undefined Response".
function incidentHeading(incident) {
  if (!incident.types?.length) return incident.title;
  return `${incident.title} - ${incident.types.join(' / ')} Response`;
}

function formatElapsed(startIso, nowMs) {
  const totalSeconds = Math.max(0, Math.floor((nowMs - new Date(startIso).getTime()) / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
}

function ChecklistChip({ label, checked, disabled, onClick }) {
  const Icon = checked ? CheckCircle2 : Circle;
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors ${
        checked ? 'bg-green-50 border-green-300 text-green-700 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20' : 'bg-neutral-50 border-neutral-200 text-neutral-500 hover:border-neutral-300 dark:bg-ink-800 dark:border-neutral-700 dark:text-neutral-500 dark:hover:border-neutral-600'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}

export default function TeamLeaderView() {
  const [modal, setModal] = useState(null); // 'teamStatus' | 'resource' | 'broadcast'
  const [showMap, setShowMap] = useState(true);
  const [incident, setIncident] = useState(null);
  const [roster, setRoster] = useState([]);
  const [rawMembers, setRawMembers] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);
  const [rolesMember, setRolesMember] = useState(null); // roster row currently open in the roles modal
  const [pendingRoles, setPendingRoles] = useState([]);
  const [zones, setZones] = useState([]);
  const [checklistMember, setChecklistMember] = useState(null); // roster row currently open in the donning-check modal
  const [doffingMember, setDoffingMember] = useState(null); // roster row currently open in the doffing-screen modal
  const ppeWarnedRef = useRef(new Set()); // `${id}-${ppeDonnedAt}` keys already toasted this session
  const queueRef = useRef(createSerialQueue()); // one member's updates reach the server in order

  useEffect(() => {
    ZoneService.getAll()
      .then((res) => setZones(res?.data || []))
      .catch((error) => console.log(error));
  }, []);

  const closeAndToast = (message) => {
    setModal(null);
    toast.success(message);
  };

  useEffect(() => {
    const tickId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickId);
  }, []);

  // Client-side half of the PPE-duration warning (the other half is a server-side push
  // via ppeTimerService, for when nobody's looking at this screen) — toast the moment
  // the countdown crosses the threshold, once per donning session.
  useEffect(() => {
    roster.forEach((m) => {
      if (!m.ppeDonnedAt) return;
      const remaining = ppeRemainingMinutes(m.ppeDonnedAt, now, incident?.ppeSafeLimitMinutes);
      const key = `${m.id}-${m.ppeDonnedAt}`;
      if (remaining <= PPE_WARNING_MINUTES && !ppeWarnedRef.current.has(key)) {
        ppeWarnedRef.current.add(key);
        toast.warning(`${m.name}'s PPE has ~${remaining} min remaining.`);
      }
    });
  }, [now, roster, incident]);

  const patchMember = async (id, uiPatch, apiPatch) => {
    const previous = roster.find((m) => m.id === id);
    setRoster((prev) => prev.map((m) => (m.id === id ? { ...m, ...uiPatch } : m)));
    try {
      await queueRef.current(id, () => AnnouncementMemberService.update(id, apiPatch));
    } catch (error) {
      console.log(error);
      // Roll back the optimistic update — e.g. a rejected zone entry must not keep
      // showing the member as "inside" with a timer running.
      if (previous) setRoster((prev) => prev.map((m) => (m.id === id ? previous : m)));
      toast.error(error?.response?.data?.message || 'Failed to save update. Please retry.');
    }
  };

  const toggleRoleConfirmed = (member) => {
    if (!member.hasRole) return;
    const roleConfirmed = !member.roleConfirmed;
    patchMember(member.id, { roleConfirmed }, { roleConfirmed });
  };

  const updateZone = (member, zoneId) => {
    const zoneName = zones.find((z) => z.id === zoneId)?.zone_name || null;
    patchMember(member.id, { zoneId, zoneName }, { zoneId });
  };

  const saveChecklist = (patch) => {
    const member = checklistMember;
    if (!member) return;
    setChecklistMember(null);
    patchMember(member.id, patch, patch);
  };

  const canEnterZone = (m) => zoneEntryBlocker(m) === null;

  const enterZone = (member) => {
    if (!canEnterZone(member)) return;
    const ppeDonnedAt = new Date().toISOString();
    patchMember(member.id, { ppeDonnedAt }, { ppeDonnedAt });
  };

  // Exiting stops the PPE timer and immediately opens the doffing screening — most
  // contamination incidents happen on the way out, so this isn't left as a
  // come-back-to-it-later step.
  const exitZone = (member) => {
    const exitedZoneAt = new Date().toISOString();
    patchMember(member.id, { ppeDonnedAt: null, exitedZoneAt }, { ppeDonnedAt: null, exitedZoneAt });
    setDoffingMember({ ...member, ppeDonnedAt: null, exitedZoneAt });
  };

  const saveDoffing = (patch) => {
    const member = doffingMember;
    if (!member) return;
    setDoffingMember(null);
    patchMember(member.id, patch, patch);
  };

  const openRolesModal = (member) => {
    setRolesMember(member);
    setPendingRoles(member.roles || []);
  };

  const saveRoles = async () => {
    const member = rolesMember;
    if (!member) return;
    setRolesMember(null);
    setRoster((prev) => prev.map((m) => (m.id === member.id ? { ...m, roles: pendingRoles } : m)));
    try {
      await AnnouncementMemberService.updateRoles(member.id, pendingRoles);
    } catch (error) {
      console.log(error);
      toast.error('Failed to save roles. Please retry.');
    }
  };

  // Sets arrival time (ata) — labeled "Mark Arrived" to match ResponderRoster.jsx
  // (Incident.jsx's Manage Roster). Previously labeled "Mark Active" here, which
  // collided with the unrelated onSceneStatus Active/Standby concept used elsewhere.
  const markArrived = (member) => {
    const activeSince = new Date().toISOString();
    patchMember(member.id, { activeSince }, { ata: activeSince });
  };

  const updateSafeLimit = async (minutes) => {
    if (!incident) return;
    const previous = incident;
    setIncident((prev) => ({ ...prev, ppeSafeLimitMinutes: minutes }));
    try {
      await AnnouncementService.update({ id: incident.id, ppeSafeLimitMinutes: minutes });
      toast.success(`Safe operating time set to ${minutes} minutes.`);
    } catch (error) {
      console.log(error);
      setIncident(previous);
      toast.error(error?.response?.data?.message || 'Failed to update safe operating time.');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const commandResponse = await CommandCenterService.getStats();
        const activeIncident = commandResponse?.data?.activeIncident || null;
        setIncident(activeIncident);

        if (activeIncident) {
          const { entity } = await AnnouncementMemberService.findById(activeIncident.id);
          setRoster(buildRoster(entity || [], activeIncident.lat, activeIncident.lng));
          setRawMembers(entity || []);
        } else {
          setRoster([]);
          setRawMembers([]);
        }
      } catch (error) {
        console.log(error);
      }
    };

    load().finally(() => setLoading(false));
    const intervalId = setInterval(load, 15000);
    return () => clearInterval(intervalId);
  }, []);

  if (loading) {
    return (
      <Sidebar title="Team Leader">
        <PageLoader label="Loading team leader view…" />
      </Sidebar>
    );
  }

  return (
    <Sidebar title="Team Leader">
      <section className="space-y-6">
        <div className="bg-seha-500 text-white rounded-xl p-6 shadow-panel flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 relative overflow-hidden">
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <span className={`text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${incident?.isMajor ? 'bg-red-500' : 'bg-neutral-500'}`}>
                {incident ? (incident.isMajor ? 'Major Incident' : 'Active Incident') : 'No Active Incident'}
              </span>
              {incident && (
                <span className="text-seha-100 text-xs font-medium tracking-wide">{incident.refCode}</span>
              )}
            </div>
            <h2 className="text-2xl font-bold tracking-tight">
              {incident ? incidentHeading(incident) : 'No active response in progress'}
            </h2>
            <p className="text-seha-100 text-sm mt-1 font-medium">
              {incident
                ? `Team Leader: ${incident.teamLeader?.fullname || 'not assigned'}`
                : 'Team Leader view with METHANE, live team GPS, distance, readiness and resources.'}
            </p>
          </div>
          <div className="relative z-10 flex flex-wrap gap-3">
            <Button variant="secondary" className="shadow-sm" onClick={() => setModal('teamStatus')}>
              Update Status
            </Button>
            <Button variant="inverse" onClick={() => setModal('resource')}>
              Request Resources
            </Button>
            <Button variant="inverse" onClick={() => setModal('broadcast')}>
              Broadcast Team
            </Button>
          </div>
        </div>

        {incident && (
          <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle p-5 flex flex-col sm:flex-row sm:items-center gap-4 dark:bg-ink-900 dark:border-neutral-700">
            <div className="flex-1">
              <h3 className="font-bold text-neutral-800 text-sm dark:text-neutral-100">Operating Time Control</h3>
              <p className="text-xs text-neutral-500 mt-1 dark:text-neutral-500">
                Sets how long members may stay donned before doffing is required — applies to everyone on this incident.
              </p>
            </div>
            <select
              value={incident.ppeSafeLimitMinutes || 30}
              onChange={(e) => updateSafeLimit(Number(e.target.value))}
              className="form-input sm:w-80"
            >
              {SAFE_LIMIT_OPTIONS.map((opt) => (
                <option key={opt.minutes} value={opt.minutes}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        <div className={`grid grid-cols-1 gap-6 ${showMap ? 'xl:grid-cols-3' : ''}`}>
          {showMap && (
            <div className="xl:col-span-2 h-[470px] bg-white rounded-xl border border-neutral-200 shadow-subtle overflow-hidden relative dark:bg-ink-900 dark:border-neutral-700">
              {incident ? (
                <IncidentMap
                  lat={Number(incident.lat)}
                  lng={Number(incident.lng)}
                  details={incident.title}
                  members={rawMembers}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-sm font-medium text-neutral-400 dark:text-neutral-500">
                  No active incident to display on the map
                </div>
              )}
            </div>
          )}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-subtle flex flex-col dark:bg-ink-900 dark:border-neutral-700">
            <div className="px-5 py-4 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between gap-3">
              <h3 className="font-bold text-neutral-800 text-sm dark:text-neutral-100">Team Members</h3>
              <button
                type="button"
                onClick={() => setShowMap((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-600 hover:border-seha-300 hover:text-seha-700 transition-colors dark:border-neutral-700 dark:bg-ink-900 dark:text-neutral-300 dark:hover:text-seha-300"
              >
                {showMap ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showMap ? 'Hide Map' : 'Show Map'}
              </button>
            </div>
            <div
              className={`overflow-y-auto p-4 grid gap-4 ${showMap ? 'grid-cols-1 h-[406px]' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 max-h-[75vh]'}`}
            >
              {roster.length > 0 ? (
                roster.map((m) => {
                  const allReady = m.roleConfirmed
                    && isChecklistComplete(m.donningChecklist)
                    && !!m.activeSince;
                  const checklistDone = checklistDoneCount(m.donningChecklist);
                  const statusDotCls = STATUS_DOT_CLS[m.status] || 'bg-yellow-500';
                  const statusBorderCls = STATUS_BORDER_CLS[m.status] || 'border-yellow-400';
                  const ppeRemaining = ppeRemainingMinutes(m.ppeDonnedAt, now, incident?.ppeSafeLimitMinutes);

                  return (
                    <div
                      key={m.id}
                      className={`p-5 rounded-2xl border-l-[6px] border border-neutral-100 hover:border-neutral-200 space-y-3 dark:border-neutral-800 dark:hover:border-neutral-700 ${statusBorderCls}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-seha-50 text-seha-700 border border-seha-100 flex items-center justify-center font-bold text-lg shrink-0 dark:bg-seha-500/10 dark:text-seha-300">
                          {m.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-lg font-bold text-neutral-800 truncate dark:text-neutral-100">{m.name}</p>
                          <p className="text-sm text-neutral-500 truncate dark:text-neutral-500">{m.role} &bull; {m.distance} &bull; {m.battery}</p>
                        </div>
                        {allReady && (
                          <span className="text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 uppercase tracking-wider shrink-0 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20">
                            Ready
                          </span>
                        )}
                        <div className={`w-3.5 h-3.5 rounded-full ring-2 ring-white shrink-0 ${statusDotCls}`} />
                      </div>

                      <div className="flex flex-wrap items-center gap-2 pl-[68px]">
                        <ChecklistChip
                          label={m.hasRole ? 'Role' : 'No role'}
                          checked={m.roleConfirmed}
                          disabled={!m.hasRole}
                          onClick={() => toggleRoleConfirmed(m)}
                        />
                        <select
                          value={m.zoneId || ''}
                          disabled={!!m.ppeDonnedAt}
                          onChange={(e) => updateZone(m, e.target.value ? Number(e.target.value) : null)}
                          className="text-xs font-semibold rounded-full border px-2.5 py-1 bg-neutral-50 border-neutral-200 text-neutral-600 disabled:opacity-60 dark:bg-ink-800 dark:border-neutral-700 dark:text-neutral-300"
                        >
                          <option value="">No zone</option>
                          {zones.map((z) => (
                            <option key={z.id} value={z.id}>{z.zone_name}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setChecklistMember(m)}
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                            checklistDone === DONNING_CHECKLIST_ITEMS.length
                              ? 'bg-green-50 border-green-300 text-green-700 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20'
                              : 'bg-neutral-50 border-neutral-200 text-neutral-500 hover:border-neutral-300 dark:bg-ink-800 dark:border-neutral-700 dark:text-neutral-500 dark:hover:border-neutral-600'
                          }`}
                        >
                          <ClipboardCheck className="w-3.5 h-3.5" />
                          Donning {checklistDone}/{DONNING_CHECKLIST_ITEMS.length}
                        </button>
                        {m.ppeDonnedAt ? (
                          <button
                            type="button"
                            onClick={() => exitZone(m)}
                            className="inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 text-orange-700 px-2.5 py-1 text-xs font-semibold dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20"
                          >
                            <LogOut className="w-3.5 h-3.5" />
                            Exit Zone
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => enterZone(m)}
                            disabled={!canEnterZone(m)}
                            title={zoneEntryBlocker(m) || ''}
                            className="inline-flex items-center gap-1 rounded-full border border-seha-200 bg-seha-50 text-seha-700 px-2.5 py-1 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed dark:bg-seha-500/10 dark:text-seha-300 dark:border-seha-500/20"
                          >
                            <LogIn className="w-3.5 h-3.5" />
                            Enter Zone
                          </button>
                        )}
                        {hasPendingDoffing(m) && (
                          <button
                            type="button"
                            onClick={() => setDoffingMember(m)}
                            className="inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 text-red-700 px-2.5 py-1 text-xs font-semibold animate-pulse dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20"
                          >
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Doffing Pending
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openRolesModal(m)}
                          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                            m.roles.length > 0
                              ? 'bg-seha-50 border-seha-200 text-seha-700 dark:bg-seha-500/10 dark:text-seha-300 dark:border-seha-500/20'
                              : 'bg-neutral-50 border-neutral-200 text-neutral-500 hover:border-neutral-300 dark:bg-ink-800 dark:border-neutral-700 dark:text-neutral-500 dark:hover:border-neutral-600'
                          }`}
                        >
                          <Tag className="w-3.5 h-3.5" />
                          {m.roles.length > 0 ? m.roles.join(', ') : 'Assign roles'}
                        </button>
                        {ppeRemaining !== null && (
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${
                              ppeRemaining <= PPE_WARNING_MINUTES
                                ? 'bg-red-50 border-red-300 text-red-700 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20'
                                : 'bg-orange-50 border-orange-200 text-orange-700 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20'
                            }`}
                          >
                            <AlertTriangle className="w-3.5 h-3.5" />
                            PPE {ppeRemaining <= 0 ? 'expired' : `${ppeRemaining}m left`}
                          </span>
                        )}
                        {m.activeSince ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-seha-200 bg-seha-50 text-seha-700 px-2.5 py-1 text-xs font-semibold">
                            <Clock className="w-3.5 h-3.5" />
                            Arrived {formatElapsed(m.activeSince, now)}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => markArrived(m)}
                            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white text-neutral-500 hover:border-seha-300 hover:text-seha-700 px-2.5 py-1 text-xs font-semibold dark:border-neutral-700 dark:bg-ink-900 dark:text-neutral-500"
                          >
                            <Clock className="w-3.5 h-3.5" />
                            Mark Arrived
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-neutral-400 text-center py-10 col-span-full dark:text-neutral-500">No team assigned</div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Modal
        open={modal === 'teamStatus'}
        onClose={() => setModal(null)}
        title="Update Team Status"
        subtitle="Team leader field status update."
        footer={null}
      >
        <div className="space-y-3">
          {STATUS_CHOICES.map((s) => (
            <button
              key={s.key}
              className={`w-full rounded-xl p-4 font-bold text-sm border-2 text-left ${s.cls}`}
              onClick={() => closeAndToast(`Team status updated: ${s.label}`)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </Modal>

      <Modal
        open={modal === 'resource'}
        onClose={() => setModal(null)}
        title="Request Resources"
        subtitle="Request PPE, detector, decon, ambulance or medication support."
        confirmLabel="Send Request"
        onConfirm={() => closeAndToast('Resource request sent to command center.')}
      >
        <div className="space-y-3">
          {['Extra Ambulance Unit', 'Hazmat PPE Kit', 'PAPR Batteries', 'Decon Lane Activation'].map((item) => (
            <label
              key={item}
              className="flex items-center gap-2 border border-neutral-200 rounded-xl bg-white p-3 text-sm font-medium text-neutral-600 dark:border-neutral-700 dark:bg-ink-900 dark:text-neutral-300"
            >
              <input type="checkbox" className="accent-seha-500" />
              {item}
            </label>
          ))}
        </div>
      </Modal>

      <Modal
        open={modal === 'broadcast'}
        onClose={() => setModal(null)}
        title="Broadcast Message"
        subtitle="Push, SMS, email, voice and staff mobile app notification."
        confirmLabel="Send Broadcast"
        onConfirm={() => closeAndToast('Broadcast sent to team.')}
      >
        <div className="space-y-5">
          <div>
            <label className="form-label" htmlFor="broadcast-target">Target</label>
            <select id="broadcast-target" className="form-input">
              <option>All active staff</option>
              <option>Team leaders only</option>
              <option>Stakeholders</option>
              <option>High Management</option>
            </select>
          </div>
          <div>
            <label className="form-label" htmlFor="broadcast-message">Message</label>
            <textarea
              id="broadcast-message"
              className="form-input"
              rows={4}
              defaultValue="Maintain zone separation. Confirm decon clearance before transfer."
            />
          </div>
        </div>
      </Modal>

      <Modal
        open={!!rolesMember}
        onClose={() => setRolesMember(null)}
        title="Assign On-Scene Roles"
        subtitle={rolesMember ? `${rolesMember.name} — can hold more than one role.` : ''}
        confirmLabel="Save"
        onConfirm={saveRoles}
      >
        <CheckboxGroup options={INCIDENT_ROLES} value={pendingRoles} onChange={setPendingRoles} />
      </Modal>

      <DonningModal member={checklistMember} onClose={() => setChecklistMember(null)} onSave={saveChecklist} />
      <DoffingModal member={doffingMember} onClose={() => setDoffingMember(null)} onSave={saveDoffing} />
    </Sidebar>
  );
}
